<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import InputText from '@/Components/Input/Text.vue';
import { onClickOutside } from '@vueuse/core';
import { usePage } from '@inertiajs/vue3';
import { THEMES } from '@/constants';
import CourseKeywords from '@/Json/autocomplete/course.json';
import ProviderKeywords from '@/Json/autocomplete/provider.json';
import ScholarshipKeywords from '@/Json/autocomplete/scholarship.json';
import CareerKeywords from '@/Json/autocomplete/career.json';

const page = usePage();
const context = computed(() => page.props?.context);
const theme = context.value.theme;

const keyword = defineModel({type: String});
const autocompletions = ref([]);
const autocompleteList = ref(null);
const emit = defineEmits('search');

const props = defineProps({
  placeholder: {
    type: String,
    default: '',
  },
  activeTab: {
    type: String,
    required: true,
  }
});

const autocompleteKeyword = (event) => {
  let keywords = [];
  if(props.activeTab == 'courses'){
    keywords = CourseKeywords;
  }
  if(props.activeTab == 'providers'){
    keywords = ProviderKeywords;
  }
  if(props.activeTab == 'scholarships'){
    keywords = ScholarshipKeywords;
  }
  if(props.activeTab == 'careers'){
    keywords = CareerKeywords;
  }
  const term = event.target.value;
  if (autocompletions.value?.length > 0 && (event.code === 'ArrowDown' || event.code === 'ArrowUp')) {
    event.preventDefault();
    autocompleteList.value?.firstElementChild.focus();
  }
  if (keyword.value?.length > 2) {
    autocompletions.value = keywords.filter(k => k.label.toLowerCase().includes(term.toLowerCase()));
  } else {
    autocompletions.value = [];
  }
};

const closeAutocompletions = () => autocompletions.value = [];
const selectAutocompletion = (autocompleteOption) => {
  emit('autocompleteSelected', autocompleteOption);
  closeAutocompletions();
};

const nextAutocompletion = (el) => el.target.nextElementSibling ? el.target.nextElementSibling.focus() : autocompleteList.value?.firstElementChild.focus();
const previousAutocompletion = (el) => el.target.previousElementSibling ? el.target.previousElementSibling.focus() : autocompleteList.value?.lastElementChild.focus();

const showAutocomplete = ref(false);

const keywordElement = ref(null);

onClickOutside(keywordElement, () => {
  showAutocomplete.value = false;
});

const handleEscape = (event) => {
  if (event.key === 'Escape' || event.code === 'Escape') {
    document.getElementById('query').focus();
    showAutocomplete.value = false;
  }
};

onMounted(() => {
  window.addEventListener('keydown', handleEscape);
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleEscape);
});

const liClasses = computed(() => [
  'p-2 mb-1 cursor-pointer transition-colors duration-100',
  theme == THEMES.STUDY_AUS && 'hover:bg-primary hover:text-white',
  theme == THEMES.STUDY_MELB && 'hover:bg-grey-200',
]);

const inputClasses = computed(() => [
  '',
  theme == THEMES.STUDY_MELB && 'focus:outline-white outline-1 outline-offset-0 border-2',
]);
</script>


<template>
  <div class="relative">
    <div class="relative flex">
      <InputText
        ref="keywordElement"
        v-model="keyword"
        class="w-full"
        :input-class="inputClasses"
        label="Keyword"
        :hide-label="true"
        autocomplete="off"
        name="query"
        :placeholder="placeholder"
        @click="showAutocomplete = true"
        @keyup="autocompleteKeyword"
        @keydown:enter="emit('search')"
      />
    </div>
    <div
      v-if="autocompletions?.length > 0 && showAutocomplete"
      class="border border-solid border-primary absolute rounded-md z-50 bg-white py-2"
    >
      <ul ref="autocompleteList">
        <li
          v-for="(option, optionIndex) in autocompletions"
          :key="`autocomplete-option-${optionIndex}`"
          :class="liClasses"
          tabindex="-1"
          @click="() => selectAutocompletion(option)"
          @keydown.down.prevent="nextAutocompletion"
          @keydown.up.prevent="previousAutocompletion"
          @keydown.enter="() => selectAutocompletion(option)"
          @keydown.esc="closeAutocompletions"
        >
          {{ option.label }}
        </li>
      </ul>
    </div>
  </div>
</template>